import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { DollarSign, FileText, Trophy, TrendingUp, Plus, ArrowUpRight, ArrowDownRight, Inbox, HardHat, PenTool } from 'lucide-react'
import { PageHeader } from '@/components/common/page-header'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { useAuthStore } from '@/store/auth-store'
import { useOpportunityStore } from '@/store/opportunity-store'
import {
  AreaChart, Area, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts'
import { CHART_COLORS } from '@/lib/chart-theme'
import { dashboardService, DashboardStats, SubmittedEstimate } from '@/services/dashboard-service'
import { proposalsService } from '@/services/proposals-service'
import type { ProposalRecord } from '@/types'
import { OnboardingChecklist } from '@/components/dashboard/onboarding-checklist'
import { getOnboardingStatus, isOnboardingDismissed } from '@/services/onboarding-service'

// --- Status helpers (manager/admin layout) ---

function proposalDisplayStatus(p: ProposalRecord): string {
  if (p.signedAt) return 'Signed'
  if (p.expiresAt && new Date(p.expiresAt) < new Date() && !p.signedAt) return 'Expired'
  if (p.viewedAt) return 'Viewed'
  if (p.sentToClientAt) return 'Sent'
  return 'Draft'
}

const statusVariant = (status: string) => {
  switch (status) {
    case 'Signed': return 'success'
    case 'Sent': return 'info'
    case 'Viewed': return 'warning'
    case 'Draft': return 'secondary'
    case 'Expired': return 'destructive'
    default: return 'secondary'
  }
}

// --- Category donut colors ---
const CATEGORY_COLORS: Record<string, string> = {
  asphalt: '#D447FB',
  concrete: '#8B47FB',
  'seal-coat': '#0074F4',
  striping: '#FF677B',
  grading: '#7CDBE7',
}

const CATEGORY_COLOR_LIST = ['#D447FB', '#8B47FB', '#0074F4', '#FF677B', '#7CDBE7']

// --- Custom Tooltip ---

function ChartTooltip({ active, payload, label }: { active?: boolean; payload?: Array<{ name: string; value: number; color: string }>; label?: string }) {
  if (!active || !payload?.length) return null
  return (
    <div className="dash-tooltip">
      <p className="dash-tooltip-label">{label}</p>
      {payload.map((entry, i) => (
        <div key={i} className="dash-tooltip-row">
          <span className="dash-tooltip-dot" style={{ backgroundColor: entry.color }} />
          <span className="dash-tooltip-name">{entry.name}:</span>
          <span className="dash-tooltip-value">{typeof entry.value === 'number' && entry.name === 'Revenue' ? `$${entry.value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : entry.value}</span>
        </div>
      ))}
    </div>
  )
}

// --- Donut Tooltip ---

function DonutTooltip({ active, payload }: { active?: boolean; payload?: Array<{ name: string; value: number; fill?: string; payload?: { fill?: string } }> }) {
  if (!active || !payload?.length) return null
  const entry = payload[0]
  const color = entry.fill || entry.payload?.fill
  return (
    <div className="dash-tooltip">
      <div className="dash-tooltip-row">
        {color && <span className="dash-tooltip-dot" style={{ backgroundColor: color }} />}
        <span className="dash-tooltip-name">{entry.name}</span>
        <span className="dash-tooltip-value">${Number(entry.value).toLocaleString()}</span>
      </div>
    </div>
  )
}

// --- Stat Card ---

function DashStat({ title, value, icon: Icon, trend, iconColor }: {
  title: string
  value: string
  icon: React.ElementType
  trend?: { value: number; positive: boolean }
  iconColor: string
}) {
  return (
    <Card className="hover:shadow-card-hover transition-shadow">
      <CardContent className="p-5">
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <p className="text-sm text-muted-foreground">{title}</p>
            <p className="text-2xl font-bold tracking-tight">{value}</p>
          </div>
          <div className="rounded-xl p-2.5" style={{ backgroundColor: `${iconColor}14` }}>
            <Icon className="h-5 w-5" style={{ color: iconColor }} />
          </div>
        </div>
        {trend && (
          <div className="mt-2 flex items-center gap-1 text-xs">
            {trend.positive ? (
              <ArrowUpRight className="h-3.5 w-3.5 text-success" />
            ) : (
              <ArrowDownRight className="h-3.5 w-3.5 text-destructive" />
            )}
            <span className="text-foreground">
              {trend.value}%
            </span>
            <span className="text-muted-foreground">vs last month</span>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

// --- Submitted for Approval Card (Admin/Manager) ---

function SubmittedForApprovalCard({ items }: { items: SubmittedEstimate[] }) {
  const navigate = useNavigate()
  const visible = items.slice(0, 6)
  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between pb-2">
        <div>
          <CardTitle className="text-base">Submitted for Approval</CardTitle>
          <p className="text-sm text-muted-foreground">Estimates awaiting review</p>
        </div>
        <div className="flex items-center gap-2">
          {items.length > 0 && (
            <Badge variant="destructive">{items.length}</Badge>
          )}
          {items.length > 0 && (
            <span className="dash-view-all" onClick={() => navigate('/proposals?tab=pending_approval')}>View All</span>
          )}
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {visible.length > 0 ? (
          <div className="divide-y divide-border border-y border-border">
            {visible.map((est) => (
              <div
                key={est.id}
                className="dash-submitted-item"
                onClick={() => navigate(`/estimates/${est.id}`)}
              >
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-sm truncate">{est.title || 'Untitled'}</p>
                  <p className="text-xs text-muted-foreground">Estimator: {est.estimatorName}</p>
                </div>
                <div className="flex flex-col items-end ml-3 shrink-0">
                  <span className="text-sm font-semibold">${Number(est.total).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</span>
                  <span className="text-xs text-muted-foreground">{formatShortDate(est.createdAt)}</span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center h-[180px] text-muted-foreground gap-2">
            <FileText className="h-8 w-8 opacity-30" />
            <span className="text-sm">No estimates waiting for approval</span>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

// --- RFPs Assigned to You Card (Estimator, used in manager layout too) ---

// function _AssignedRfpsCard({ items }: { items: AssignedRfp[] }) {
//   const navigate = useNavigate()
//   return (
//     <Card>
//       <CardHeader className="flex flex-row items-start justify-between pb-2">
//         <div>
//           <CardTitle className="text-base">RFPs Assigned to You</CardTitle>
//           <p className="text-sm text-muted-foreground">Pending requests for proposals</p>
//         </div>
//         {items.length > 0 && (
//           <Badge variant="secondary" className="ml-2">{items.length}</Badge>
//         )}
//       </CardHeader>
//       <CardContent className="pt-0">
//         {items.length > 0 ? (
//           <div className="divide-y divide-border border-y border-border">
//             {items.map((rfp) => (
//               <div
//                 key={rfp.id}
//                 className="dash-rfp-item"
//                 onClick={() => navigate(`/rfp-requests/${rfp.id}`)}
//               >
//                 <div className="min-w-0 flex-1">
//                   <p className="font-medium text-sm truncate">{rfp.title}</p>
//                   <p className="text-xs text-muted-foreground">
//                     {rfp.rfpNumber && <span className="prop-id mr-2">{rfp.rfpNumber}</span>}
//                     {formatShortDate(rfp.createdAt)}
//                   </p>
//                 </div>
//                 {rfp.isOverdue && (
//                   <span className="dash-overdue">
//                     <AlertCircle className="h-3 w-3" />
//                     Overdue
//                   </span>
//                 )}
//               </div>
//             ))}
//           </div>
//         ) : (
//           <div className="flex items-center justify-center h-[120px] text-sm text-muted-foreground">
//             No RFPs assigned yet
//           </div>
//         )}
//       </CardContent>
//     </Card>
//   )
// }

function WinsByCategoryCard({ data, centered }: { data: DashboardStats['winsByCategory']; centered?: boolean }) {
  const totalRevenue = data.reduce((s, d) => s + d.revenue, 0)
  const enrichedData = data.map(d => ({ ...d, fill: CATEGORY_COLORS[d.type] || CATEGORY_COLOR_LIST[0] }))

  return (
    <Card className={centered ? 'dash-wins-card' : ''}>
      <CardHeader className="pb-2">
        <div>
          <CardTitle className="text-base">Wins by Category</CardTitle>
          <p className="text-sm text-muted-foreground">Signed revenue by project type</p>
        </div>
      </CardHeader>
      <CardContent className={centered ? 'dash-wins-content' : 'pt-0'}>
        {data.length > 0 ? (
          <div className={centered ? 'dash-wins-center' : ''}>
            <ResponsiveContainer width="100%" height={180}>
              <PieChart>
                <Pie
                  data={enrichedData}
                  cx="50%"
                  cy="50%"
                  innerRadius={45}
                  outerRadius={72}
                  dataKey="revenue"
                  nameKey="label"
                  stroke="none"
                >
                  {enrichedData.map((entry) => (
                    <Cell key={entry.type} fill={entry.fill} />
                  ))}
                </Pie>
                <Tooltip content={<DonutTooltip />} />
              </PieChart>
            </ResponsiveContainer>
            <div className="dash-cat-legend">
              {data.map((entry) => {
                const pct = totalRevenue > 0 ? Math.round((entry.revenue / totalRevenue) * 100) : 0
                return (
                  <div key={entry.type} className="dash-cat-legend-item">
                    <span className="dash-cat-legend-dot" style={{ backgroundColor: CATEGORY_COLORS[entry.type] || CATEGORY_COLOR_LIST[0] }} />
                    <span className="dash-cat-legend-label">{entry.label}</span>
                    <span className="dash-cat-legend-pct">{pct}%</span>
                  </div>
                )
              })}
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center h-[220px] text-sm text-muted-foreground">
            No signed proposals yet
          </div>
        )}
      </CardContent>
    </Card>
  )
}

// --- Annual Revenue Chart ---

function AnnualRevenueCard({ data, colSpan, title = 'Annual Company Revenue' }: { data: DashboardStats['monthlyRevenue']; colSpan?: boolean; title?: string }) {
  return (
    <Card className={colSpan ? 'lg:col-span-2' : ''}>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <div>
          <CardTitle className="text-base">{title}</CardTitle>
          <p className="text-sm text-muted-foreground">Monthly signed proposal revenue</p>
        </div>
        <div className="flex gap-3 text-xs text-muted-foreground">
          <span className="flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full" style={{ backgroundColor: CHART_COLORS.blue }} />
            {new Date().getFullYear()}
          </span>
          <span className="flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full" style={{ backgroundColor: CHART_COLORS.purple, opacity: 0.6 }} />
            {new Date().getFullYear() - 1}
          </span>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <ResponsiveContainer width="100%" height={280}>
          <AreaChart data={data} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
            <defs>
              <linearGradient id="gradBlue" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={CHART_COLORS.blue} stopOpacity={0.25} />
                <stop offset="95%" stopColor={CHART_COLORS.blue} stopOpacity={0} />
              </linearGradient>
              <linearGradient id="gradPurple" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={CHART_COLORS.purple} stopOpacity={0.15} />
                <stop offset="95%" stopColor={CHART_COLORS.purple} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
            <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#CBD5E1' }} />
            <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#CBD5E1' }} tickFormatter={(v: number) => v >= 1000 ? `$${v / 1000}K` : `$${v}`} />
            <Tooltip content={<ChartTooltip />} />
            <Area type="monotone" dataKey="lastYear" name={`${new Date().getFullYear() - 1}`} stroke={CHART_COLORS.purple} strokeWidth={2} strokeOpacity={0.6} fill="url(#gradPurple)" dot={{ r: 4, fill: '#fff', stroke: CHART_COLORS.purple, strokeWidth: 2, strokeOpacity: 0.6 }} activeDot={{ r: 5, fill: '#fff', stroke: CHART_COLORS.purple, strokeWidth: 2 }} />
            <Area type="monotone" dataKey="thisYear" name={`${new Date().getFullYear()}`} stroke={CHART_COLORS.blue} strokeWidth={2.5} fill="url(#gradBlue)" dot={{ r: 4, fill: '#fff', stroke: CHART_COLORS.blue, strokeWidth: 2 }} activeDot={{ r: 5, fill: '#fff', stroke: CHART_COLORS.blue, strokeWidth: 2 }} />
          </AreaChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  )
}

// --- Helpers ---

function formatCurrency(value: number): string {
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(2)}M`
  if (value >= 1_000) return `$${(value / 1_000).toFixed(1)}K`
  return `$${value.toLocaleString()}`
}

function formatProposalValue(p: ProposalRecord): string {
  const total = p.estimate?.total ?? p.estimatedValue ?? 0
  return `$${Number(total).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
}

function formatShortDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

function getCurrentMonthName(): string {
  return new Date().toLocaleDateString('en-US', { month: 'long' })
}

// --- Main Dashboard ---

export function DashboardPage() {
  const user = useAuthStore((s) => s.user)
  const navigate = useNavigate()
  const userRole = user?.role || ''
  const isEstimator = userRole === 'ESTIMATOR'
  const isAdmin = userRole === 'ADMIN' || userRole === 'SUPERADMIN'
  const [stats, setStats] = useState<DashboardStats | null>(null)
  const [proposals, setProposals] = useState<ProposalRecord[]>([])
  const setRfpQueueCount = useOpportunityStore((s) => s.setRfpQueueCount)

  // Onboarding gate — only admins go through this
  const [dashboardUnlocked, setDashboardUnlocked] = useState(() => {
    if (!user?.tenantId) return false // user not loaded yet — wait for effect below
    if (!isAdmin) return true         // non-admins skip the gate
    return isOnboardingDismissed(user)
  })
  const [showChecklist, setShowChecklist] = useState(false)

  // Re-evaluate once the auth store has hydrated the user
  useEffect(() => {
    if (!user?.tenantId) return
    if (!isAdmin) {
      setDashboardUnlocked(true)
      return
    }
    const alreadyDismissed = isOnboardingDismissed(user)
    if (alreadyDismissed) {
      setDashboardUnlocked(true)
    } else {
      setShowChecklist(true)
    }
  }, [user?.tenantId, user?.onboardingDismissed]) // eslint-disable-line react-hooks/exhaustive-deps

  // If onboarding status check fails entirely, don't permanently block the dashboard
  useEffect(() => {
    if (!isAdmin || !user?.tenantId || dashboardUnlocked) return
    getOnboardingStatus()
      .catch(() => setDashboardUnlocked(true))
  }, [isAdmin, user?.tenantId]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleOnboardingUnlocked = useCallback(() => {
    setDashboardUnlocked(true)
  }, [])

  const handleOnboardingDismissed = useCallback(() => {
    setShowChecklist(false)
  }, [])

  useEffect(() => {
    dashboardService.getStats().then((s) => {
      setStats(s)
      if (isEstimator) setRfpQueueCount(s.rfpQueueCount ?? 0)
    }).catch(console.error)
    // Manager/admin layout needs recent proposals
    if (!isEstimator) {
      proposalsService.list(0, 50).then((r) => setProposals(r.proposals)).catch(() => setProposals([]))
    }
  }, [isEstimator, setRfpQueueCount])

  const recentProposals = [...proposals].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()).slice(0, 5)
  const monthName = getCurrentMonthName()

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader title="Dashboard">
        {dashboardUnlocked && (
          <Button onClick={() => navigate('/estimates?new=1')}>
            <Plus className="mr-2 h-4 w-4" />
            Create New Estimate
          </Button>
        )}
      </PageHeader>

      {/* Onboarding checklist — shown to admins until dismissed or all required steps done */}
      {showChecklist && (
        <OnboardingChecklist onUnlocked={handleOnboardingUnlocked} onDismissed={handleOnboardingDismissed} />
      )}

      {/* Dashboard content — gated until pricing + branding are set up */}
      {!dashboardUnlocked ? null : (
        <>

      {/* Stat Cards — shared by all roles */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <DashStat
          title={`${monthName} Revenue`}
          value={stats ? formatCurrency(stats.currentMonthRevenue) : '—'}
          icon={DollarSign}
          iconColor={CHART_COLORS.blue}
          trend={stats?.totalRevenue.trend ?? undefined}
        />
        <DashStat
          title="Total Bids"
          value={stats ? String(stats.totalBids.value) : '—'}
          icon={FileText}
          iconColor={CHART_COLORS.purple}
          trend={stats?.totalBids.trend ?? undefined}
        />
        <DashStat
          title="Bids Won"
          value={stats ? String(stats.bidsWon.value) : '—'}
          icon={Trophy}
          iconColor="#f1586c"
          trend={stats?.bidsWon.trend ?? undefined}
        />
        <DashStat
          title="Close Rate"
          value={stats ? `${stats.closeRate.value}%` : '—'}
          icon={TrendingUp}
          iconColor={CHART_COLORS.sky}
          trend={stats?.closeRate.trend ?? undefined}
        />
      </div>

      {isEstimator ? (
        /* ─── ESTIMATOR LAYOUT ─── */
        <>
          {/* Row 1: Annual Revenue + Waiting for Signoff */}
          <div className="grid gap-6 lg:grid-cols-3">
            <AnnualRevenueCard data={stats?.monthlyRevenue ?? []} colSpan title="Your Annual Sales" />

            {/* Waiting for Signoff */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between px-6 pb-2 space-y-0">
                <div className="flex items-center gap-2">
                  <PenTool className="h-4 w-4" style={{ color: '#a855f7' }} />
                  <CardTitle className="text-base">Waiting for Signoff</CardTitle>
                </div>
                <span className="dash-view-all" onClick={() => navigate('/proposals')}>View All</span>
              </CardHeader>
              <CardContent className="pt-0 flex flex-col flex-1">
                {stats && stats.waitingForSignatureCount === 0 ? (
                  <div className="dash-pipeline-empty" style={{ minHeight: 160 }}>
                    <PenTool className="h-5 w-5 opacity-30" />
                    <p>Everything is Signed, Sealed and Delivered!</p>
                  </div>
                ) : (
                  <div className="divide-y divide-border border-y border-border">
                    {(stats?.waitingForSignatureItems ?? []).slice(0, 4).map((prop) => (
                      <div key={prop.id} className="flex items-center justify-between py-3 px-1 hover:bg-accent/50 cursor-pointer transition-colors" onClick={() => navigate('/proposals')}>
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-sm truncate">{prop.title}</p>
                          <p className="text-xs text-muted-foreground">
                            {prop.proposalNumber && <span className="prop-id mr-2">{prop.proposalNumber}</span>}
                            {prop.clientName}
                          </p>
                        </div>
                        <span className="text-sm font-semibold ml-3 shrink-0">{formatCurrency(prop.estimatedValue)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Row 2: Opportunity Queue + Estimates in Progress + Wins by Category */}
          <div className="grid gap-6 sm:grid-cols-3">
            {/* Opportunity Queue */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between px-6 pb-2 space-y-0">
                <div className="flex items-center gap-2">
                  <Inbox className="h-4 w-4" style={{ color: '#f97316' }} />
                  <CardTitle className="text-base">Opportunity Queue</CardTitle>
                </div>
                <span className="dash-view-all" onClick={() => navigate('/rfp-requests')}>View All</span>
              </CardHeader>
              <CardContent className="pt-0">
                {stats && stats.rfpQueueCount === 0 ? (
                  <div className="dash-pipeline-empty">
                    <Inbox className="h-5 w-5 opacity-30" />
                    <p>You're all caught up!</p>
                  </div>
                ) : (
                  <div className="divide-y divide-border border-y border-border">
                    {(stats?.rfpQueueItems ?? []).slice(0, 4).map((rfp) => (
                      <div key={rfp.id} className="flex items-center justify-between py-3 px-1 hover:bg-accent/50 cursor-pointer transition-colors" onClick={() => navigate(`/rfp-requests/${rfp.id}`)}>
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-sm truncate">{rfp.title}</p>
                          {rfp.rfpNumber && <p className="text-xs text-muted-foreground"><span className="prop-id">{rfp.rfpNumber}</span></p>}
                        </div>
                        <span className="text-xs text-muted-foreground ml-3 shrink-0">{new Date(rfp.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Estimates in Progress */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between px-6 pb-2 space-y-0">
                <div className="flex items-center gap-2">
                  <HardHat className="h-4 w-4" style={{ color: '#3b82f6' }} />
                  <CardTitle className="text-base">Estimates in Progress</CardTitle>
                </div>
                <span className="dash-view-all" onClick={() => navigate('/estimates')}>View All</span>
              </CardHeader>
              <CardContent className="pt-0">
                {stats && stats.estimatesInProgressCount === 0 ? (
                  <div className="dash-pipeline-empty">
                    <HardHat className="h-5 w-5 opacity-30" />
                    <p>No active estimates. Get on it!</p>
                  </div>
                ) : (
                  <div className="divide-y divide-border border-y border-border">
                    {(stats?.estimatesInProgressItems ?? []).slice(0, 4).map((est) => (
                      <div key={est.id} className="flex items-center justify-between py-3 px-1 hover:bg-accent/50 cursor-pointer transition-colors" onClick={() => navigate(`/estimates/${est.id}`)}>
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-sm truncate">{est.title}</p>
                          {est.clientName && <p className="text-xs text-muted-foreground">{est.clientName}</p>}
                        </div>
                        <span className="text-sm font-semibold ml-3 shrink-0">{formatCurrency(est.total)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <WinsByCategoryCard data={stats?.winsByCategory ?? []} />
          </div>
        </>
      ) : (
        /* ─── MANAGER / ADMIN LAYOUT ─── */
        <>
          {/* Row 1: Revenue trend + Submitted for Approval */}
          <div className="grid gap-6 lg:grid-cols-3">
            <AnnualRevenueCard data={stats?.monthlyRevenue ?? []} colSpan />
            <SubmittedForApprovalCard items={stats?.submittedForApproval ?? []} />
          </div>

          {/* Row 2: Wins by Category + Recent Proposals */}
          <div className="grid gap-6 lg:grid-cols-3">
            <WinsByCategoryCard data={stats?.winsByCategory ?? []} />

            <Card className="lg:col-span-2">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <div>
                  <CardTitle className="text-base">Recent Proposals</CardTitle>
                  <p className="text-sm text-muted-foreground">Latest proposal activity</p>
                </div>
                <span className="dash-view-all" onClick={() => navigate('/proposals')}>View All</span>
              </CardHeader>
              <CardContent className="pt-0">
                {recentProposals.length > 0 ? (
                  <div className="divide-y divide-border border-y border-border">
                    {recentProposals.map((p) => {
                      const status = proposalDisplayStatus(p)
                      return (
                        <div
                          key={p.id}
                          className="flex items-center justify-between py-3 px-1 hover:bg-accent/50 cursor-pointer transition-colors"
                          onClick={() => navigate(`/proposals/${p.id}`)}
                        >
                          <div className="min-w-0 flex-1">
                            <p className="font-medium text-sm truncate">{p.estimate?.title || p.proposalNumber || 'Untitled'}</p>
                            <p className="text-xs text-muted-foreground">{formatShortDate(p.createdAt)}</p>
                          </div>
                          <div className="flex items-center gap-3 ml-3">
                            <span className="text-sm font-semibold hidden sm:block">{formatProposalValue(p)}</span>
                            <Badge variant={statusVariant(status) as any}>{status}</Badge>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-[120px] text-sm text-muted-foreground">
                    No proposals yet. Create an estimate and generate a proposal to get started.
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </>
      )}

        </>
      )}

    </div>
  )
}
